import { hexToRgba } from './color'

/** klinecharts' default area/line color when none is configured. The default
 *  area gradient (`getAlphaBlue(0.01)` → `getAlphaBlue(0.2)`) is this blue, so
 *  using it as the fallback keeps the look identical until a user edits it. */
const DEFAULT_LINE_COLOR = '#1677ff'

type AreaBackground = string | Array<{ offset: number; color: string }>

type AreaPoint = { show?: boolean; color: string; rippleColor: string; animation: boolean }

/**
 * Last-price marker for the area/line series: a solid dot with a static halo.
 *
 * klinecharts ships `point.animation: true`, which pulses the halo on an
 * `iterationCount: Infinity` rAF loop whose every frame calls
 * `updatePane(UpdateLevel.Main)` — a full main-pane repaint, forever, per
 * chart, even when nothing is happening. Candle types have no such loop, which
 * is why only line/area charts get slow, and why a multi-chart grid switched to
 * line multiplies the cost by cell count. Freezing the halo drops it entirely.
 *
 * Colors are tinted from the series line color; klinecharts' defaults are a
 * hardcoded blue that desyncs from a customized line.
 */
function areaPoint(lineColor: string): AreaPoint {
  return {
    color: lineColor,
    rippleColor: hexToRgba(lineColor, 0.3),
    animation: false,
  }
}

/**
 * Map the toolbar chart type to a klinecharts `candle` style patch.
 *
 * 'line' is not a native klinecharts type — it's the area type with the
 * gradient fill removed, leaving just the close-price line. Both 'line' and
 * 'area' take their line color from `lineColor` (the per-chart "Line color"
 * setting); the area fill gradient is tinted from that same color so the two
 * read as one series. setStyles merges patches, so switching line ↔ area must
 * explicitly restore/blank the gradient.
 */
export function candleStylesForChartType(
  chartType: string,
  lineColor: string = DEFAULT_LINE_COLOR,
): {
  type: string
  area?: { lineColor: string; backgroundColor: AreaBackground; point: AreaPoint }
} {
  // 'tpo' renders the same way as 'line' — the Market Profile blocks ARE the
  // price rendering (drawn by the internal TPO indicator), and the bare close
  // line sits on top as a path reference. Candles would fight the profile for
  // the same space, so there are none.
  //
  // The one difference is the last-price point: klinecharts draws a dot plus a
  // halo at the end of an area series, which reads as a useful marker on a line
  // chart but as an unexplained blob sitting on top of the TPO blocks. `show:
  // false` turns it off outright rather than painting it transparent, so the
  // ripple geometry is skipped too.
  // Heikin Ashi paints its own candles from an internal indicator (see
  // heikinAshiIndicator.ts), so the native series is hidden outright: an area
  // type (a valid CandleType, so klinecharts never sees an unknown string) with
  // a transparent line, no fill and no last-price dot. The native last-price
  // LINE and high/low marks still draw at the raw prices, which is correct.
  if (chartType === 'heikin_ashi' || chartType === 'heikin_ashi_hollow') {
    return {
      type: 'area',
      area: {
        lineColor: 'transparent',
        backgroundColor: 'transparent',
        point: { ...areaPoint(lineColor), show: false },
      },
    }
  }
  if (chartType === 'line' || chartType === 'tpo') {
    return {
      type: 'area',
      area: {
        lineColor,
        backgroundColor: 'transparent',
        point: { ...areaPoint(lineColor), show: chartType !== 'tpo' },
      },
    }
  }
  if (chartType === 'area') {
    return {
      type: 'area',
      area: {
        lineColor,
        backgroundColor: [
          { offset: 0, color: hexToRgba(lineColor, 0.01) },
          { offset: 1, color: hexToRgba(lineColor, 0.2) },
        ],
        point: areaPoint(lineColor),
      },
    }
  }
  return { type: chartType }
}
